<template>
  <div class="cm-donut-card">
    <div :id="`donut-tooltip-${UUID}`" class="cm-donut-card__tooltip">
      <p><span id="value"></span></p>
    </div>

    <svg
      :id="`donut-${UUID}`"
      :width="size"
      :height="size"
    ></svg>
    <table>
      <thead>
        <tr>
          <td
            v-for="(col, colIndex) in cols"
            :key="colIndex"
          >
            {{ col.label }}
          </td>
        </tr>
      </thead>

      <tbody>
        <tr
          v-for="(row, rowIndex) in value"
          :key="rowIndex"
        >
          <td
            v-for="(td, tdIndex) in cols"
            :key="tdIndex"
            :class="[
              {'legend-name-td': tdIndex === rowKey},
              {'percent-td': td.isPercent},
              td.className
            ]"
          >
            <template v-if="tdIndex === rowKey">
              <i
                class="legend-circle"
                :style="{borderColor: value[rowIndex].fillColor}"
              ></i>
              <span
                :title="row.title"
                @click="toggleFromChart(rowIndex)"
              >
                {{ row[rowKey] }}
              </span>
            </template>
            <template v-else>
              <b>{{ fNum.format(value[rowIndex][tdIndex]) }}</b>
              <i v-if="td.isPercent">
                ({{ value[rowIndex][`${tdIndex}_percent`] }}%)
              </i>
            </template>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import * as d3 from "d3";
import uuidMixin from "../d3_uuid_mixin";

export default {
  name: "CmDonutCard",

  mixins: [uuidMixin],

  props: {
    value: {
      type: Array
    },
    centerLabel: {
      type: String,
      default: ""
    },
    centerValue: {
      type: Number
    },
    rowKey: {
      type: String,
      default: "text"
    },
    countKey: {
      type: String,
      default: "count"
    },
    cols: {
      type: Object
    },
    colors: {
      type: Array,
      default: () => ["#FFDA83", "#00CA69", "#7370FF", "#FF8373", "#56D9FE", "#A7A7A7"]
    },
    getTooltipTemplate: {
      type: Function
    },
    limit: {
      type: Number
    },
    size: {
      type: Number,
      default: 180
    }
  },

  data() {
    return {
      colorScale: d3.scaleOrdinal(this.colors),
      chunkData: []
    }
  },

  mounted() {
    this.generateChart(this.value);
  },

  methods: {
    toggleFromChart(rowIndex) {
      // TO DO - make excludable some parts
      // this.chunkData = this.value.slice(0).splice(0, rowIndex);
      // this.generateChart(this.chunkData);
    },

    generateChart(data) {
      d3.selectAll(`#donut-${this.UUID} *`).remove();

      const that = this;

      const mainEl = d3.select(`#donut-${this.UUID}`),
            width = mainEl.attr("width"),
            height = mainEl.attr("height"),
            radius = Math.min(width, height) / 2;

      const pie = d3.pie(data).value(d => d[this.countKey]).sort(null);
      const arc = d3.arc().outerRadius(radius).innerRadius(radius * 0.7);

      // set colors
      data.forEach(i => {
        i.fillColor = this.colorScale(i[this.rowKey]);
      });

      this.$forceUpdate();

      // filter for shadow
      const defs = mainEl.append("defs");
      const filter = defs.append("filter")
                      .attr("id", "drop-shadow")
                      .attr("height","130%");

      filter.append("feGaussianBlur")
              .attr("in","SourceAlpha")
              .attr("stdDeviation", 3)
              .attr("result", "blur");

      filter.append("feOffset")
          .attr("in", "blur")
          .attr("dx", 2)
          .attr("dy", 2)
          .attr("result", "offsetBlur");

      const feMerge = filter.append("feMerge");

      feMerge.append("feMergeNode")
          .attr("in", "offsetBlur")
      feMerge.append("feMergeNode")
          .attr("in", "SourceGraphic");

      // filter for shadow is ended

      const svg = mainEl.append("g")
                        .attr("transform", `translate(${width / 2}, ${height / 2})`);
      const g = svg.selectAll(".arc")
                  .data(pie(data))
                  .enter()
                  .append("g")
                  .attr("class", "arc");

      g.append("path")
        .attr("d", arc)
        .attr("class", "arc")
        .style("fill", (d, i) => d.data.fillColor)
        .on("mousemove", function(e, d) {
          d3.select(this)
            .attr("stroke","#7370FF")
            .attr("stroke-width","2px")
            .style("filter", "url(#drop-shadow)")
            .attr("transform", "scale(1.08)");

          svg.selectAll("path").sort((a, b) => a.index !== d.index ? -1 : 1);
          that.showTooltip(d, [e.clientX, e.clientY]);
        })
        .on("mouseout", function() {
          d3.select(this)
              .attr("stroke","none")
              .style("filter","none");

					d3.select(this)
						.transition()
						.duration(100)
            .attr('transform','scale(1)');

          d3.select(`#donut-tooltip-${that.UUID}`)
            .style("display", "none");
        });

      // label in the center

      const centerSvg = svg.append("g");

      centerSvg.append('circle')
				.attr('fill','transparent')
        .attr('r', radius / 1.5);

			centerSvg.append('text')
        .style("font-size", "13px")
				.attr("transform", `translate(0,-${radius / 3.5})`)
				.attr("text-anchor", "middle")
        .html("Всего");

      centerSvg.append('text')
        .style("font-weight", "bold")
        .attr("text-anchor", "middle")
        .attr("transform", `translate(0,${radius / 10})`)
        .html(this.fNum.format(this.centerValue));

      centerSvg.append('text')
        .style("font-size", "13px")
				.attr("transform", `translate(0,${radius / 3})`)
				.attr("text-anchor", "middle")
        .html(this.centerLabel);
    },

    showTooltip(info, coords) {
      if (!this.getTooltipTemplate) return;

      const { data } = info;
      let tooltipHtml = `<div class="tooltip-container">
        ${this.getTooltipTemplate(data)}
      </div>`;

      d3.select(`#donut-tooltip-${this.UUID}`).html(tooltipHtml)
        .style("left", coords[0] + 17 + "px")
        .style("top", coords[1] + 10 + "px")
        .style("display", "block");
    }
  }
}
</script>

<style lang="scss">
.cm-donut-card {
  display: flex;
  align-items: center;
  width: 100%;
  svg {
    overflow: visible !important;
  }

  &__tooltip {
    background: $color_white;
    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
    border-radius: 4px;
    position: fixed;
    display: none;
    z-index: 999;
    .tooltip-container {
      display: flex;
      flex-direction: column;
      font-size: 12px;
      padding: 15px 10px;
      h4 {
        font-weight: 500;
        margin: 0 0 5px;
      }
      span {
        font-weight: 300;
        b {
          font-weight: 600;
        }
      }
      .tooltip-dates {
        display: flex;
        flex-direction: column;
        overflow-y: hidden;
        max-height: 100px;
      }
    }
  }

  .arc {
    transition: all 0.3s ease;
  }

  text {
    fill: rgba(0, 0, 0, 0.6);
  }

  table {
    margin-left: 30px;
    line-height: 130%;
    tbody {
      tr {
        vertical-align: baseline;
      }
      td:first-of-type {
        position: relative;
        padding-left: 28px;
      }
    }
    
    .legend-circle {
      background: $color_white;
      border-width: 3px;
      border-style: solid;
      border-color: $color_black;
      border-radius: 50%;
      display: inline-block;
      width: 13px;
      height: 13px;
      position: absolute;
      top: 10px;
      left: 0px;
    }

    td {
      padding: 8px 0 8px 16px;
    }

    td:not(:first-of-type) {
      text-align: right;
    }


    thead {
      td {
        font-weight: bold;
      }
      td:first-of-type {
        padding-left: 30px;
      }
    }

    .percent-td {
      position: relative;
      white-space: nowrap;
      i {
        color: rgba(#9D9B9B, 0.9);
        font-style: normal;
      }
    }

    .legend-name-td {
      cursor: context-menu;
      &--dots-overflow {
        @include overflow-ellipsis;
      }
    }
  }
}
</style>
